import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  IconButton, TablePagination, alpha, CircularProgress, Alert, Menu, MenuItem, TextField, FormControl, InputLabel, Select,
  useTheme, useMediaQuery
} from '@mui/material';
import { Plus, MoreVertical, Trash2, Edit, Map } from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import ExportTableButton from '../components/common/ExportTableButton';

const StatesView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [formData, setFormData] = useState({
    state_name: '',
    country_id: '',
    state_status: 'Activated'
  });

  // Action Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionState, setActionState] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadStates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/admin/states', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch
        }
      });
      setStates(response.data.states || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to fetch states');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  const loadCountries = async () => {
    try {
      const response = await axios.get('/country');
      setCountries(response.data || []);
    } catch (err) {
      console.error('Failed to load countries', err);
    }
  };

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  useEffect(() => {
    loadCountries();
  }, []);

  const handleMenuOpen = (event, stateItem) => {
    setAnchorEl(event.currentTarget);
    setActionState(stateItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionState(null);
  };

  const handleOpenModal = (stateItem = null) => {
    if (stateItem) {
      setEditingState(stateItem);
      setFormData({
        state_name: stateItem.state_name,
        country_id: stateItem.country_id?._id || stateItem.country_id || '',
        state_status: stateItem.state_status || 'Activated'
      });
    } else {
      setEditingState(null);
      setFormData({
        state_name: '',
        country_id: countries[0]?._id || '',
        state_status: 'Activated'
      });
    }
    setError(null);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (actionState) {
      try {
        setLoading(true);
        await axios.delete(`/admin/states/${actionState._id}`);
        setDeleteDialogOpen(false);
        setActionState(null);
        loadStates();
      } catch (err) {
        setError(err.response?.data?.msg || 'Failed to delete state');
        setDeleteDialogOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (editingState) {
        await axios.put(`/admin/states/${editingState._id}`, formData);
      } else {
        await axios.post('/admin/states', formData);
      }
      setIsModalOpen(false);
      loadStates();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save state');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="States"
        subtitle="Manage state, province, and regional jurisdiction entities"
        count={total}
        countLabel="states"
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => handleOpenModal()}
          >
            New State
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Toolbar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search state..."
          hasActiveFilters={Boolean(searchTerm)}
          onResetFilters={() => { setSearchTerm(''); setPage(0); }}
        >
          <ExportTableButton
            data={states}
            total={total}
            onExportAll={async () => {
              const res = await axios.get('/admin/states', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                },
              });
              return res.data.states || [];
            }}
            columns={[
              { label: 'State Name', key: 'state_name' },
              { label: 'Country', value: (row) => row.country_id?.country_name || 'N/A' },
              { label: 'Status', key: 'state_status' },
            ]}
            filename="states"
          />
        </FilterBar>

        {error && (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead sx={{ backgroundColor: 'background.neutral' }}>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>State Name</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Country</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {states.length > 0 ? states.map((s) => (
                <TableRow key={s._id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{s.state_name}</TableCell>
                  <TableCell>{s.country_id?.country_name || 'N/A'}</TableCell>
                  <TableCell>
                    <StatusBadge
                      status={s.state_status === 'Activated' ? 'active' : 'inactive'}
                      label={s.state_status || 'Activated'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, s)}>
                      <MoreVertical size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ p: 0 }}>
                    <EmptyState
                      icon={Map}
                      title="No states found"
                      description="Try adjusting your search query or add a new state."
                      actionLabel="Add State"
                      onAction={() => handleOpenModal()}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: { sx: { width: 140, p: 0.5 } }
        }}
      >
        <MenuItem onClick={() => handleOpenModal(actionState)} sx={{ fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / Edit Modal Dialog */}
      <AdminDialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm">
        <form onSubmit={handleFormSubmit}>
          <AdminDialogHeader
            title={editingState ? 'Edit State' : 'New State'}
            subtitle="Manage regional state and province territory"
            icon={Map}
            onClose={() => setIsModalOpen(false)}
          />
          <AdminDialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
              )}
              <TextField
                label="State Name"
                fullWidth
                required
                value={formData.state_name}
                onChange={(e) => setFormData({ ...formData, state_name: e.target.value })}
                placeholder="e.g. Maharashtra, California, Ontario"
              />

              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.country_id}
                  label="Country"
                  onChange={(e) => setFormData({ ...formData, country_id: e.target.value })}
                >
                  {countries.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.country_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.state_status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, state_status: e.target.value })}
                >
                  <MenuItem value="Activated">Activated</MenuItem>
                  <MenuItem value="Deactivated">Deactivated</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </AdminDialogContent>
          <AdminDialogActions>
            <Button onClick={() => setIsModalOpen(false)} color="inherit" disabled={loading} sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading && <CircularProgress size={16} color="inherit" />}
              sx={{ px: 3, fontWeight: 700 }}
            >
              {loading ? 'Saving...' : 'Save State'}
            </Button>
          </AdminDialogActions>
        </form>
      </AdminDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        itemName={actionState?.state_name}
        itemType="State"
      />
    </Box>
  );
};

export default StatesView;
